import type { RequestOptions } from '@@/plugin-request/request';
import type { RequestConfig } from '@umijs/max';
import { request as umiRequest, history } from '@umijs/max';
import { message } from 'antd';
import {
  getAccessToken,
  getRefreshToken,
  setTokens,
  clearTokens,
} from '@/utils/auth';
import { refreshToken as refreshTokenApi } from '@/services/uuc/auth';

/**
 * 后端统一返回体
 * 与 com.uuc.common.result.Result 对齐
 */
interface ResponseStructure<T = any> {
  code: number;
  message: string;
  data: T;
}

/** 登录路径 */
const LOGIN_PATH = '/user/login';

/** 重试标记，避免刷新 Token 后再次进入刷新逻辑 */
const RETRY_FLAG = '__uucRetryFlag';

/**
 * 自定义业务错误
 * - code: 后端业务码
 * - message: 错误信息
 * - data: 后端返回的 data（可选）
 */
export class BizError extends Error {
  code: number;
  data?: any;
  constructor(code: number, message: string, data?: any) {
    super(message);
    this.name = 'BizError';
    this.code = code;
    this.data = data;
  }
}

/** 是否正在刷新 Token，避免并发多次刷新 */
let isRefreshing = false;
/** 等待刷新完成的请求队列：刷新成功后用新 Token 重试 */
let pendingQueue: Array<{
  resolve: (token: string) => void;
  reject: (error: Error) => void;
}> = [];

/**
 * 通知队列中所有等待请求：刷新成功，用新 Token 重试
 */
function notifyQueueSuccess(newToken: string) {
  pendingQueue.forEach((cb) => cb.resolve(newToken));
  pendingQueue = [];
}

/**
 * 通知队列中所有等待请求：刷新失败，全部拒绝
 */
function notifyQueueFail(error: Error) {
  pendingQueue.forEach((cb) => cb.reject(error));
  pendingQueue = [];
}

/**
 * 调用刷新 Token 接口
 * 复用 auth.ts 的 refreshToken 函数，避免重复维护接口路径
 */
async function doRefreshToken(refreshToken: string): Promise<string> {
  try {
    const resp = await refreshTokenApi(refreshToken);
    // [变更] 成功码统一为 0（原 200 与后端不一致）
    if (resp?.code !== 0 || !resp.data) {
      throw new Error(resp?.message || '刷新失败');
    }
    setTokens(resp.data.accessToken, resp.data.refreshToken);
    return resp.data.accessToken;
  } catch (e: any) {
    throw new Error(e?.message || '刷新 Token 失败');
  }
}

/**
 * @name 请求配置
 * 适配后端 { code, message, data } 返回体
 * - 请求拦截器：自动注入 Authorization
 * - 响应拦截器：code != 200 抛业务错误；401 尝试刷新 Token 并重试
 */
export const errorConfig: RequestConfig = {
  // 错误处理
  errorConfig: {
    // 错误抛出：从后端响应体中识别业务错误
    errorThrower: (res) => {
      const body = res as unknown as ResponseStructure;
      // [变更] 后端成功码是 0（Result.code=0），原 code !== 200 会把所有成功响应当成错误抛出
      if (body && typeof body.code === 'number' && body.code !== 0) {
        throw new BizError(body.code, body.message || '请求失败', body.data);
      }
    },
    // 错误处理
    errorHandler: (error: any, opts: any) => {
      if (opts?.skipErrorHandler) throw error;
      // 业务错误
      if (error instanceof BizError) {
        // 401 已在响应拦截器里处理刷新，这里兜底跳登录
        if (error.code === 401) {
          message.error(error.message || '登录已失效，请重新登录');
          clearTokens();
          history.push(LOGIN_PATH);
          return;
        }
        message.error(error.message || '请求失败');
        return;
      }
      // HTTP 错误
      if (error?.response) {
        const status = error.response.status;
        if (status === 401) {
          // 拦截器已处理，这里兜底
          message.error('登录已失效，请重新登录');
          clearTokens();
          history.push(LOGIN_PATH);
          return;
        }
        if (status === 403) {
          message.error('没有权限访问');
          return;
        }
        if (status === 404) {
          message.error('请求资源不存在');
          return;
        }
        if (status >= 500) {
          message.error('服务器异常，请稍后再试');
          return;
        }
        message.error(`请求失败（${status}）`);
        return;
      }
      if (error?.request) {
        message.error('网络异常，请检查网络');
        return;
      }
      message.error(error?.message || '请求异常');
    },
  },

  // 请求拦截器：注入 Token
  requestInterceptors: [
    (config: RequestOptions) => {
      const token = getAccessToken();
      const headers: any = config.headers || {};
      if (token) {
        headers.Authorization = `Bearer ${token}`;
      }
      return { ...config, headers };
    },
  ],

  // 响应拦截器：处理 code != 200 与 401 刷新
  responseInterceptors: [
    async (response: any) => {
      // 非 JSON 直接返回（如文件下载）
      const contentType = response?.headers?.['content-type'] || '';
      if (!contentType.includes('application/json')) {
        return response;
      }
      const body = response?.data as ResponseStructure | undefined;
      // 不是统一返回体的，直接返回
      if (!body || typeof body.code !== 'number') {
        return response;
      }
      // 业务正常直接放行
      if (body.code === 0) {
        return response;
      }

      // 401：尝试刷新 Token
      if (body.code === 401) {
        const refreshTokenStr = getRefreshToken();
        const url: string = response.config?.url || '';
        // 重试请求再次 401，直接跳登录（避免死循环）
        if (response.config?.[RETRY_FLAG]) {
          clearTokens();
          history.push(LOGIN_PATH);
          return Promise.reject(new BizError(401, body.message || '登录已失效'));
        }
        // 刷新接口本身失败或没有 refreshToken，直接跳登录
        if (url.includes('/auth/refresh') || !refreshTokenStr) {
          clearTokens();
          history.push(LOGIN_PATH);
          return Promise.reject(new BizError(401, body.message || '登录失效'));
        }

        // 重试请求的 config：保留原 url/method/data/params 等
        const retryRequest = (newToken: string) => {
          const cfg = { ...response.config };
          cfg.headers = { ...(cfg.headers || {}), Authorization: `Bearer ${newToken}` };
          cfg[RETRY_FLAG] = true;
          // 用 umi request 重发，会走完所有拦截器（但带了 RETRY_FLAG 不会再次触发刷新）
          return umiRequest(cfg.url, cfg);
        };

        // 避免并发刷新
        if (!isRefreshing) {
          isRefreshing = true;
          try {
            const newToken = await doRefreshToken(refreshTokenStr);
            isRefreshing = false;
            // 通知队列里的请求重试
            notifyQueueSuccess(newToken);
            // 重放当前请求
            return await retryRequest(newToken);
          } catch (e: any) {
            isRefreshing = false;
            notifyQueueFail(e instanceof Error ? e : new Error(String(e)));
            clearTokens();
            history.push(LOGIN_PATH);
            return Promise.reject(new BizError(401, '登录已失效，请重新登录'));
          }
        }

        // 进入等待队列：等刷新完成后用新 Token 重试
        return new Promise((resolve, reject) => {
          pendingQueue.push({
            resolve: async (newToken: string) => {
              try {
                const retried = await retryRequest(newToken);
                resolve(retried);
              } catch (err) {
                reject(err);
              }
            },
            reject: (err: Error) => reject(err),
          });
        });
      }

      // 其他业务错误：交给 errorHandler
      return response;
    },
  ],
};
